/** @jsx jsx */
import { jsx } from "theme-ui";
import Divider from "../../elements/divider";
import { pulseLine, lightRay } from "./animations";

interface ConstellationProps {
  offset: number;
  factor?: number;
}

const Constellation = ({ offset, factor = 1.5 }: ConstellationProps) => {
  const stars = [
    { x: "12%", y: "20%", size: 2.5, color: "rgba(255, 255, 255, 0.8)", bright: true },
    { x: "20%", y: "28%", size: 2, color: "rgba(200, 220, 255, 0.75)", bright: false },
    { x: "35%", y: "25%", size: 3, color: "rgba(255, 200, 150, 0.8)", bright: true },
    { x: "45%", y: "35%", size: 2.5, color: "rgba(255, 255, 255, 0.7)", bright: false },
    { x: "60%", y: "40%", size: 2.5, color: "rgba(186, 85, 211, 0.75)", bright: false },
    { x: "75%", y: "38%", size: 2, color: "rgba(255, 255, 255, 0.7)", bright: false },
    { x: "25%", y: "55%", size: 2, color: "rgba(200, 220, 255, 0.7)", bright: false },
    { x: "45%", y: "65%", size: 3, color: "rgba(255, 200, 150, 0.8)", bright: true },
    { x: "65%", y: "62%", size: 2.5, color: "rgba(255, 255, 255, 0.75)", bright: false },
    { x: "80%", y: "70%", size: 2, color: "rgba(255, 255, 255, 0.65)", bright: false },
    { x: "15%", y: "45%", size: 2, color: "rgba(30, 144, 255, 0.7)", bright: false },
    { x: "55%", y: "20%", size: 2.5, color: "rgba(255, 140, 0, 0.75)", bright: true },
    { x: "30%", y: "15%", size: 2, color: "rgba(255, 255, 255, 0.65)", bright: false },
    { x: "85%", y: "50%", size: 2.5, color: "rgba(186, 85, 211, 0.7)", bright: false },
    { x: "5%", y: "60%", size: 2, color: "rgba(200, 220, 255, 0.65)", bright: false },
  ];

  const lines = [
    { x1: "12%", y1: "20%", x2: "20%", y2: "28%", stroke: "rgba(255, 255, 255, 0.5)", delay: 0, duration: 3 },
    { x1: "20%", y1: "28%", x2: "35%", y2: "25%", stroke: "rgba(200, 220, 255, 0.5)", delay: 0.3, duration: 3.5 },
    { x1: "35%", y1: "25%", x2: "45%", y2: "35%", stroke: "rgba(255, 200, 150, 0.5)", delay: 0.6, duration: 4 },
    { x1: "45%", y1: "35%", x2: "60%", y2: "40%", stroke: "rgba(255, 255, 255, 0.4)", delay: 0.9, duration: 3.2 },
    { x1: "60%", y1: "40%", x2: "75%", y2: "38%", stroke: "rgba(186, 85, 211, 0.4)", delay: 1.2, duration: 3.8 },
    { x1: "25%", y1: "55%", x2: "45%", y2: "65%", stroke: "rgba(200, 220, 255, 0.4)", delay: 1.5, duration: 4.2 },
    { x1: "45%", y1: "65%", x2: "65%", y2: "62%", stroke: "rgba(255, 200, 150, 0.4)", delay: 1.8, duration: 3.6 },
    { x1: "65%", y1: "62%", x2: "80%", y2: "70%", stroke: "rgba(255, 255, 255, 0.35)", delay: 2.1, duration: 3.9 },
    { x1: "15%", y1: "45%", x2: "25%", y2: "55%", stroke: "rgba(30, 144, 255, 0.4)", delay: 2.4, duration: 4.1 },
    { x1: "55%", y1: "20%", x2: "60%", y2: "40%", stroke: "rgba(255, 140, 0, 0.4)", delay: 2.7, duration: 3.7 },
  ];

  return (
    <Divider speed={0.1} offset={offset} factor={factor} zIndex={9}>
      <div
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          opacity: 0.4,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        >
          <style>
            {`
              .pulse-line-0 { animation: ${pulseLine} 3s ease-in-out infinite; animation-delay: 0s; }
              .pulse-line-1 { animation: ${pulseLine} 3.5s ease-in-out infinite; animation-delay: 0.3s; }
              .pulse-line-2 { animation: ${pulseLine} 4s ease-in-out infinite; animation-delay: 0.6s; }
              .pulse-line-3 { animation: ${pulseLine} 3.2s ease-in-out infinite; animation-delay: 0.9s; }
              .pulse-line-4 { animation: ${pulseLine} 3.8s ease-in-out infinite; animation-delay: 1.2s; }
              .pulse-line-5 { animation: ${pulseLine} 4.2s ease-in-out infinite; animation-delay: 1.5s; }
              .pulse-line-6 { animation: ${pulseLine} 3.6s ease-in-out infinite; animation-delay: 1.8s; }
              .pulse-line-7 { animation: ${pulseLine} 3.9s ease-in-out infinite; animation-delay: 2.1s; }
              .pulse-line-8 { animation: ${pulseLine} 4.1s ease-in-out infinite; animation-delay: 2.4s; }
              .pulse-line-9 { animation: ${pulseLine} 3.7s ease-in-out infinite; animation-delay: 2.7s; }
              .light-ray { animation: ${lightRay} 4s ease-in-out infinite; }
            `}
          </style>
          {lines.map((line, i) => (
            <line
              key={i}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={line.stroke}
              strokeWidth="1"
              strokeDasharray="3,3"
              filter={`drop-shadow(0 0 1px ${line.stroke.replace("0.5", "0.3").replace("0.4", "0.2").replace("0.35", "0.15")})`}
              className={`pulse-line-${i}`}
            />
          ))}
          {stars.map((star, i) => (
            <g key={i}>
              {star.bright && (
                <circle
                  cx={star.x}
                  cy={star.y}
                  r={star.size * 3}
                  fill="none"
                  stroke={star.color}
                  strokeWidth="0.5"
                  opacity="0.2"
                  className="light-ray"
                  style={{
                    animationDelay: `${i * 0.2}s`,
                    animationDuration: `${4 + (i % 2)}s`,
                  }}
                />
              )}
              <circle
                cx={star.x}
                cy={star.y}
                r={star.size}
                fill={star.color}
                style={{
                  filter: `drop-shadow(0 0 ${star.size * 1.5}px ${star.color.replace("0.", "0.9")})`,
                  animation: `twinkle ${3 + (i % 3)}s ease-in-out infinite`,
                  animationDelay: `${i * 0.25}s`,
                }}
              />
            </g>
          ))}
        </svg>
      </div>
    </Divider>
  );
};

export default Constellation;

